import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import Remove from "@mui/icons-material/Remove";
import WarningIcon from "@mui/icons-material/Warning";
import { Button, CardContent, Grid, Typography, useTheme, styled, Theme } from "@mui/material"; //CircularProgress
import { IBooking, IBookingError, IPositioningFlight } from "@vmosolutions/oc-interfaces";
import classNames from "classnames";
import { MouseEvent, useEffect, useState } from "react";
import { CrewMemberChips } from "src/components/CrewSchedule/CrewMemberChips";
import { formatDateString } from "src/components/DateDisplay";
import { SwitchField } from "src/components/FormikComponents/SwitchField";
import { PosFlightChip } from "src/components/Pairings/PosFlightChip";
import BookingIcon from "./booking_icon.svg?react";
import { BookingForm } from "./Forms/BookingForm";
import { useDispatch } from "react-redux";
import { deleteBooking } from "src/redux/actions/CrewTravel/CrewTravel.Actions";

interface IBookingProps {
  booking: IBooking | IBookingError;
  isSelected: boolean;
  displayLocatorOnly?: boolean;
  hideUTCToggle?: boolean;
  displayAsLocal?: boolean;
}

interface IBookingWrapper {
  booking: IBooking | IBookingError;
  setEditMode: (state: boolean) => void;
}

const OCBookingCard = styled(CardContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: "10px 5px",
  "&.MuiCardContent-root:last-child": { paddingBottom: "10px" },
  "&.edit": {
    padding: "4px 0px",
    "&.MuiCardContent-root:last-child": { paddingBottom: "4px" },
  },
  "&.selected": {},
  "& .booking_icon": {
    width: 40,
    margin: "2px 5px 2px 0px",
  },
  "& .section": {
    display: "flex",
    margin: "0px 5px",
    "&.details": {
      width: 150,
      alignItems: "flex-start",
      "& .locator": {
        fontSize: 14,
        textTransform: "uppercase",
        color: theme.palette.primary.contrastText,
      },
      "& .agency": {
        fontSize: 12,
        lineHeight: "14px",
        overflowWrap: "normal",
        whiteSpace: "normal",
      },
    },
    "&.flights": {
      "& .positioning_flight": {
        display: "flex",
        alignItems: "center",
        "& .booking_chip .posflightchip": {
          padding: "0px 5px", //skinny chips
        },
        "& .departure_arrival": {
          minWidth: 230,
          display: "flex",
          margin: "0px 10px",
          fontSize: 14,
          lineHeight: "18px",
          "& svg": { height: 16 },
        },
      },
      "& .warnings": {
        margin: "2px 0px",
        color: theme.palette.error.main,
        lineHeight: "12px",
        maxWidth: 350,
        "& svg": {
          width: 14,
          height: 14,
          verticalAlign: "text-bottom",
          marginRight: 5,
        },
      },
    },
  },
  "& .card_actions": {
    marginTop: 10,
    "& button.edit_booking, button.remove_booking": {
      padding: "0px 8px",
      margin: 5,
      width: "auto",
      fontSize: 12,
      borderRadius: 2,
    },
  },
}));

const BookingFormWrapper = (props: IBookingWrapper): JSX.Element => {
  const { booking, setEditMode } = props;
  return <BookingForm isNew={false} booking={booking as IBooking} closeForm={() => setEditMode(false)} />;
};

/**
 * Manage Crew Travel Bookings.
 */
export const BookingCard = (props: IBookingProps): JSX.Element => {
  const { booking, isSelected, displayLocatorOnly, hideUTCToggle, displayAsLocal } = props;
  const { locator, agency, bookingFlights, crew } = booking; //_id, custom, content
  const theme = useTheme();
  const dispatch = useDispatch();

  const [editMode, setEditMode] = useState<boolean>(false);
  const [displayAsUTC, setDisplayAsUTC] = useState<boolean>(true);

  useEffect(() => {
    if (!isSelected) {
      setEditMode(false);
    }
  }, [isSelected]);

  const handleRemoveBooking = async (id: string): Promise<any> => {
    await deleteBooking(id, dispatch);
  };

  return (
    <OCBookingCard className={classNames(isSelected && "selected", editMode && "edit")} key={booking._id}>
      {!editMode ? (
        displayLocatorOnly && !isSelected ? (
          <Grid container direction="row" alignItems="flex-start">
            <Grid item className="section details">
              <BookingIcon className="booking_icon" />
              <Grid container direction="column">
                <div className="locator">{locator}</div>
                <div className="agency">{agency?.title}</div>
              </Grid>
            </Grid>
          </Grid>
        ) : (
          <Grid container direction="row" alignItems="flex-start">
            <Grid item className="section details">
              <BookingIcon className="booking_icon" />
              <Grid container direction="column">
                <div className="locator">{locator}</div>
                <div className="agency">{agency?.title}</div>
              </Grid>
            </Grid>

            <Grid item className="section crew">
              <CrewMemberChips bookingCrew={crew} />
            </Grid>

            <Grid item className="section flights">
              <Grid container direction="column">
                <Typography variant="caption" style={{ width: 125 }}>
                  Flights
                </Typography>
                {bookingFlights?.length > 0 ? (
                  bookingFlights.map((flight, index) => {
                    const { _id, origin, destination, departure, arrival, airlineIATA, flightNumber, changedFields } =
                      flight;
                    const posFlight: IPositioningFlight = {
                      _id,
                      origin,
                      destination,
                      outTime: departure,
                      inTime: arrival,
                      airlineIATA,
                      flightNumber,
                      bookings: [],
                      isGround: false,
                      createdBy: null,
                      createdAt: null,
                    };
                    return (
                      <Grid container key={index} direction="column">
                        <Grid item className="positioning_flight">
                          <div className="booking_chip">
                            <PosFlightChip data={posFlight} />
                          </div>
                          <Typography
                            variant="body1"
                            color={
                              hideUTCToggle
                                ? !displayAsLocal
                                  ? "inherit"
                                  : "secondary"
                                : displayAsUTC
                                  ? "inherit"
                                  : "secondary"
                            }
                            className="departure_arrival"
                          >
                            {formatDateString(
                              flight.departure,
                              hideUTCToggle
                                ? !displayAsLocal
                                  ? "Etc/UTC"
                                  : flight?.originTimezoneId
                                : displayAsUTC
                                  ? "Etc/UTC"
                                  : flight?.originTimezoneId,
                              true
                            )}
                            <ArrowRightIcon />
                            {formatDateString(
                              flight.arrival,
                              hideUTCToggle
                                ? !displayAsLocal
                                  ? "Etc/UTC"
                                  : flight?.destinationTimezoneId
                                : displayAsUTC
                                  ? "Etc/UTC"
                                  : flight?.destinationTimezoneId,
                              true
                            )}
                          </Typography>
                        </Grid>
                        {changedFields?.length > 0 && (
                          <Grid item key={index} className="warnings">
                            <WarningIcon />
                            Warning: Values in
                            {changedFields.map((field: string, index: number) => {
                              const lastIndex = index === changedFields.length - 1 ? true : false;
                              return `${index === 0 ? " " : ""} ${field} ${!lastIndex ? ", " : " "}`;
                            })}
                            are mismatched with this flight in pairing.
                          </Grid>
                        )}
                      </Grid>
                    );
                  })
                ) : (
                  <Typography variant="body1" className="departure_arrival">
                    None Assigned
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        )
      ) : (
        <BookingFormWrapper booking={booking} setEditMode={setEditMode} />
      )}

      {isSelected && !editMode && (
        <Grid container className="card_actions" wrap="nowrap">
          <Button
            className="edit_booking"
            variant="contained"
            color="primary"
            onClick={(event: MouseEvent) => {
              event.stopPropagation();
              setEditMode(true);
            }}
          >
            Edit
          </Button>
          {(!(booking as IBookingError).errorType || (booking as IBookingError).errorType !== "UNBOOKED_FLIGHT") && (
            <Button
              className="remove_booking"
              variant="contained"
              color="primary"
              type="button"
              onClick={() => handleRemoveBooking(booking._id)}
            >
              <Remove style={{ marginRight: 5, width: 10, height: 10 }} />
              Remove
            </Button>
          )}
          {!hideUTCToggle && (
            <Grid item>
              {/* DISPLAY DATE/TIME LOCAL UTC SWITCH */}
              <SwitchField
                className={`time_mode_selector ${!displayAsUTC && "active"}`}
                name={"editflight_time_mode_selector"}
                value={displayAsUTC}
                onChange={() => setDisplayAsUTC(!displayAsUTC)}
                switchlabels={["UTC", "LOCAL"]}
                switchtooltips={["Times as UTC", "Times as local"]}
                label="Display Date/Times in"
                allowNull={false}
              />
            </Grid>
          )}
        </Grid>
      )}
    </OCBookingCard>
  );
};
